"use client";

import { useEffect, useRef, useState } from "react";
import obituaryService from "@/services/obituary-service";
import { matchesObituaryName } from "@/utils/obituaryNameSearch";
import TextFieldComp from "@/app/components/appcomponents/TextFieldComp";

/**
 * Type a name or surname, click the person in the list. The same name matching as the Funerals page.
 * `value` is the chosen obituary (or null); `onChange` gets the chosen obituary, or null when the visitor
 * edits the text again.
 */
export default function ObituaryPicker({
  value,
  onChange,
  placeholder = "Ime in priimek pokojne/ga",
  background = "#F3F2E6",
}) {
  const [text, setText] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  // "idle" | "done" (the last search returned) | "error" (the search itself failed)
  const [searchState, setSearchState] = useState("idle");
  const searchSeq = useRef(0);

  useEffect(() => {
    const term = text.trim();
    if (value || term.length < 2) {
      setSuggestions([]);
      setSearchState("idle");
      return;
    }
    const seq = ++searchSeq.current;
    const timer = setTimeout(async () => {
      try {
        const response = await obituaryService.getObituary({ name: term, limit: 30, page: 1 });
        if (seq !== searchSeq.current) return;
        const found = response?.error ? [] : (response.obituaries ?? []);
        setSuggestions(found.filter((item) => matchesObituaryName(item, term)).slice(0, 8));
        setSearchState(response?.error ? "error" : "done");
      } catch {
        if (seq === searchSeq.current) {
          setSuggestions([]);
          setSearchState("error");
        }
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [text, value]);

  const hint =
    suggestions.length > 0 ? "Klikni ime pokojne osebe v seznamu pod poljem"
    : searchState === "error" ? "Iskanje trenutno ni na voljo"
    : text.trim().length >= 2 && searchState === "done" ? "Osebe ne najdem. Preveri črkovanje imena"
    : null;

  return (
    <div className="relative">
      <TextFieldComp
        placeholder={placeholder}
        background={background}
        value={value ? `${value.name} ${value.sirName}` : text}
        onChange={(e) => {
          if (value) onChange(null);
          setText(e.target.value);
        }}
      />
      {suggestions.length > 0 && (
        <ul className="absolute z-10 mt-1 max-h-[240px] w-full overflow-y-auto rounded-[6px] bg-[#f1fffe] py-1 shadow-lg">
          {suggestions.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => {
                  onChange(item);
                  setText(`${item.name} ${item.sirName}`.trim());
                  setSuggestions([]);
                }}
                className="w-full px-3 py-2 text-left text-[#7d7d7d] hover:bg-[#6D778E] hover:text-white"
              >
                {item.name} {item.sirName}
                {item.city ? `, ${item.city}` : item.location ? `, ${item.location}` : ""}
              </button>
            </li>
          ))}
        </ul>
      )}
      {hint && <p className="mt-1 text-[13px] leading-[16px] text-[#6D778E]">{hint}</p>}
    </div>
  );
}
